// src/pages/document/DocumentFromTemplatePage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { TagPicker } from '@/components/TagPicker';
import { TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { assignTagsToEntity } from '@/features/task/logic';
import type { Tag } from '@core/types/tag';

/**
 * Create document from template page.
 */
export const DocumentFromTemplatePage = observer(() => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const store = useDocument();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [template, setTemplate] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    department: '',
    language: '',
  });
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (templateId) {
      // Load templates to find the current one
      store.ListDocumentTemplates().then((res: any) => {
        const found = res?.templates?.find(
          (tpl: any) => (tpl.entity?.id || tpl.id) === templateId,
        );
        if (found) {
          setTemplate(found);
          setFormData((prev) => ({
            ...prev,
            title: found.default_title_pattern || '',
          }));
        }
      });
    }
  }, [templateId]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateId) return;
    setIsSubmitting(true);

    try {
      const doc = await store.CreateDocumentFromTemplate(templateId, formData as any);
      const newId = doc?.entity?.id;

      // Assign tags via TagService after document creation
      if (newId && selectedTags.length > 0) {
        await assignTagsToEntity(selectedTags, newId, 'document');
      }

      navigate('/documents');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading && !template) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/templates" className="text-muted-foreground hover:text-foreground">
            {t('document.templates')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.createFromTemplate')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.createFromTemplate')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Template info */}
      {template && (
        <Card>
          <CardHeader>
            <CardTitle>{String(template.name ?? '')}</CardTitle>
            <CardDescription>{String(template.description ?? '')}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Document Type</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {String(template.document_type ?? '')}
                </dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Numbering Scheme</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {String(template.numbering_scheme ?? '')}
                </dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                <dd className="text-sm text-foreground col-span-2">
                  <Badge variant={template.is_active ? 'success' : 'secondary'}>
                    {template.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Default Confidentiality</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {String(template.default_confidentiality ?? '')}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Document form */}
      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('document.details')}</CardTitle>
            <CardDescription>{t('document.fillInfo')}</CardDescription>
          </CardHeader>
          <CardContent>
            <TitleInput
              value={formData.title}
              onChange={(v) => handleChange('title', v)}
              placeholder={t('document.fieldTitlePlaceholder')}
              initialFocus
              onEnter={() => descRef.current?.focus()}
              className="text-xl"
            />
            <AutoGrowTextarea
              ref={descRef}
              value={formData.description}
              onChange={(v) => handleChange('description', v)}
              placeholder={t('document.fieldDescriptionPlaceholder')}
              className="mb-2"
            />

            <div className="border-t border-border pt-2 mt-2 space-y-1">
              <FormRow label={t('document.fieldDepartment')} labelWidth="sm" htmlFor="fromtpl-department">
                <input
                  id="fromtpl-department"
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder={t('document.fieldDepartmentPlaceholder')}
                  className="w-64 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </FormRow>
              <FormRow label={t('document.fieldLanguage')} labelWidth="sm" htmlFor="fromtpl-language">
                <input
                  id="fromtpl-language"
                  type="text"
                  value={formData.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  placeholder={t('document.fieldLanguagePlaceholder')}
                  className="w-64 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </FormRow>
              <FormRow label={t('task.field.tags')} labelWidth="sm">
                <TagPicker
                  value={selectedTags}
                  onChange={setSelectedTags}
                  placeholder={t('tag.selectTags')}
                />
              </FormRow>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/documents/templates">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.title.trim()}>
              {isSubmitting ? t('common.creating') : t('document.create')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default DocumentFromTemplatePage;
